import React, { type ComponentType, useCallback, useEffect, useMemo } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import {
	type EditorRemixLifecycleOwner,
	useEditorRemixLifecycleOwner,
} from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleAdapterHostProps,
	EditorRemixMachineLifecycleSession,
} from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import {
	registerEditorInlineRovoEditLifecycleAdapter,
	registerEditorInlineRovoEditLifecycleOwner,
	observeEditorInlineRovoEditProducerAttempt,
	startEditorInlineRovoEditHandoffPresentation,
} from '../utils/editor-inline-rovo-edit-lifecycle';
import { registerEditorRemixMachineLifecycleOwnership } from '../utils/editor-remix-callback-registry';

type EditorInlineRovoEditLifecycleHostProps = {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	LifecycleAdapterHost: ComponentType<EditorRemixLifecycleAdapterHostProps>;
};

/** Keeps inline-edit lifecycle admission available while Remix presentation is idle. */
export const EditorInlineRovoEditLifecycleHost = ({
	api,
	LifecycleAdapterHost,
}: EditorInlineRovoEditLifecycleHostProps): React.JSX.Element => {
	const lifecycleOwner = useEditorRemixLifecycleOwner();
	const retain = lifecycleOwner?.retain;
	const forgetMachineAuthority = lifecycleOwner?.forgetMachineAuthority;
	// Register only stable owner actions so activity changes cannot tear down an admitted attempt.
	const lifecycleOwnerForRegistration = useMemo<EditorRemixLifecycleOwner | undefined>(() => {
		if (!retain || !forgetMachineAuthority) {
			return undefined;
		}
		return {
			active: undefined,
			forgetMachineAuthority,
			retain,
		};
	}, [forgetMachineAuthority, retain]);
	const onRegisterEditorRemixMachineLifecycleOwnership = useCallback(
		(session: EditorRemixMachineLifecycleSession) =>
			registerEditorRemixMachineLifecycleOwnership(api, session),
		[api],
	);
	const onRegisterEditorInlineRovoEditLifecycleAdapter = useCallback(
		(adapter: EditorRemixLifecycleAdapter) =>
			registerEditorInlineRovoEditLifecycleAdapter(api, adapter),
		[api],
	);
	const onStartInlineRovoEditHandoffPresentation = useCallback(
		(attemptId: string) => {
			if (!api?.core.actions.execute || !api.malleableUi?.commands.startRemixThinking) {
				return false;
			}
			if (!startEditorInlineRovoEditHandoffPresentation(api, attemptId)) {
				return false;
			}
			api.core.actions.execute(
				api.malleableUi.commands.startRemixThinking({
					isUpdate: true,
					remixAttemptId: attemptId,
				}),
			);
			return true;
		},
		[api],
	);

	useEffect(() => {
		if (!lifecycleOwnerForRegistration) {
			return;
		}
		return registerEditorInlineRovoEditLifecycleOwner(api, lifecycleOwnerForRegistration);
	}, [api, lifecycleOwnerForRegistration]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditAdmission?.((targetId) => {
			try {
				return (
					api.malleableUi?.actions.beginInlineRovoEdit?.(targetId) ?? { status: 'unavailable' }
				);
			} catch {
				// The bridge is optional; unavailable lifecycle infrastructure preserves legacy submission.
				return { status: 'unavailable' };
			}
		});
	}, [api]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditProducerAttemptReporting?.(
			(observation) => observeEditorInlineRovoEditProducerAttempt(api, observation),
		);
	}, [api]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditHandoffPresentation?.(
			onStartInlineRovoEditHandoffPresentation,
		);
	}, [api, onStartInlineRovoEditHandoffPresentation]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditReporting?.(
			(attemptId, outcome) =>
				api.malleableUi?.actions.reportInlineRovoEdit?.(attemptId, outcome) ?? false,
		);
	}, [api]);

	return (
		<LifecycleAdapterHost
			isEditorRemixStateRearchitectureTreatment
			onRegisterEditorRemixMachineLifecycleOwnership={
				onRegisterEditorRemixMachineLifecycleOwnership
			}
			onRegisterEditorInlineRovoEditLifecycleAdapter={
				onRegisterEditorInlineRovoEditLifecycleAdapter
			}
		/>
	);
};
